import { useState, useEffect } from "react";

type MembershipTier = "free" | "gold";
type MembershipStatus = "active" | "inactive";

interface GoldRequest {
  id: string;
  businessName: string;
  ownerName: string;
  email: string;
  phone: string;
  city: string;
  currentMember: string;
  message: string;
  appliedAt: string;
}

interface MembershipRecord {
  id: string;
  businessName: string;
  ownerName: string;
  email: string;
  phone: string;
  city: string;
  businessType: string;
  tier: MembershipTier;
  status: MembershipStatus;
  appliedAt: string;
  activatedAt?: string;
  expiresAt?: string;
  notes?: string;
}

const MEMBERSHIP_KEY = "accommodationMemberships";
const GOLD_REQUESTS_KEY = "goldMembershipRequests";

const tierLabels: Record<MembershipTier, string> = {
  free: "Ücretsiz",
  gold: "Gold",
};

const tierColors: Record<MembershipTier, string> = {
  free: "bg-stone-100 text-stone-600",
  gold: "bg-amber-100 text-amber-700",
};

const tierIcons: Record<MembershipTier, string> = {
  free: "ri-user-line",
  gold: "ri-vip-crown-line",
};

const statusColors: Record<MembershipStatus, string> = {
  active: "bg-emerald-100 text-emerald-700",
  inactive: "bg-rose-100 text-rose-600",
};

const goldBenefits = [
  "Öne çıkan işletme rozeti",
  "Arama sonuçlarında üst sıra",
  "Sınırsız fotoğraf galerisi",
  "Özel istatistik paneli",
  "Öncelikli müşteri desteği",
  "Sosyal medya paylaşımı",
];

const freeBenefits = [
  "Temel işletme listesi",
  "Telefon ile rezervasyon",
  "Harita görünümü",
  "Misafir yorumları",
];

const MembershipManager = () => {
  const [memberships, setMemberships] = useState<MembershipRecord[]>([]);
  const [selected, setSelected] = useState<MembershipRecord | null>(null);
  const [filterTier, setFilterTier] = useState<"all" | MembershipTier>("all");
  const [filterStatus, setFilterStatus] = useState<"all" | MembershipStatus>("all");
  const [toast, setToast] = useState<string | null>(null);
  const [editNotes, setEditNotes] = useState("");
  const [showNoteEdit, setShowNoteEdit] = useState(false);
  const [activeTab, setActiveTab] = useState<"members" | "gold-requests">("members");
  const [goldRequests, setGoldRequests] = useState<GoldRequest[]>([]);
  const [approvingId, setApprovingId] = useState<string | null>(null);

  useEffect(() => {
    loadMemberships();
    loadGoldRequests();
  }, []);

  const loadGoldRequests = () => {
    const stored: GoldRequest[] = JSON.parse(localStorage.getItem(GOLD_REQUESTS_KEY) || "[]");
    // Add some mock gold requests for demo
    if (stored.length === 0) {
      const mockRequests: GoldRequest[] = [
        {
          id: "gr-1",
          businessName: "Ölüdeniz Blue Bay Hotel",
          ownerName: "Can Arslan",
          email: "info@bluebayoludeniz.com",
          phone: "0252 617 00 55",
          city: "Muğla (Bodrum/Marmaris)",
          currentMember: "Evet, ücretsiz üyeyim",
          message: "Gold üyeliğe geçmek istiyoruz. Fiyat bilgisi alabilir miyiz?",
          appliedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        },
        {
          id: "gr-2",
          businessName: "Safranbolu Konak Otel",
          ownerName: "Fatma Kaya",
          email: "info@safrankonakhotel.com",
          phone: "0370 712 55 66",
          city: "Karabük",
          currentMember: "Evet, ücretsiz üyeyim",
          message: "",
          appliedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        },
      ];
      localStorage.setItem(GOLD_REQUESTS_KEY, JSON.stringify(mockRequests));
      setGoldRequests(mockRequests);
    } else {
      setGoldRequests(stored);
    }
  };

  const handleApproveGoldRequest = (req: GoldRequest) => {
    // Find matching membership and upgrade to gold
    const now = new Date();
    const expires = new Date(now);
    expires.setFullYear(expires.getFullYear() + 1);
    const updated = memberships.map((m) =>
      m.businessName === req.businessName
        ? { ...m, tier: "gold" as MembershipTier, status: "active" as MembershipStatus, activatedAt: now.toISOString(), expiresAt: expires.toISOString() }
        : m
    );
    setMemberships(updated);
    localStorage.setItem(MEMBERSHIP_KEY, JSON.stringify(updated));
    // Remove from requests
    const remaining = goldRequests.filter((r) => r.id !== req.id);
    setGoldRequests(remaining);
    localStorage.setItem(GOLD_REQUESTS_KEY, JSON.stringify(remaining));
    setApprovingId(req.id);
    setTimeout(() => setApprovingId(null), 2000);
    showToast(`"${req.businessName}" Gold üyeliğe yükseltildi! E-posta taslağı açılıyor...`);

    // Otomatik e-posta taslağı aç
    const expiresFormatted = expires.toLocaleDateString("tr-TR");
    const subject = encodeURIComponent(`🏆 Gold Üyeliğiniz Aktif! — ${req.businessName}`);
    const body = encodeURIComponent(
      `Sayın ${req.ownerName},\n\n` +
      `Tebrikler! "${req.businessName}" işletmenizin Gold üyelik başvurusu onaylanmıştır.\n\n` +
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n` +
      `🏆 GOLD ÜYELİK AKTİVASYON BİLGİLERİ\n` +
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n` +
      `✅ Aktivasyon Tarihi: ${now.toLocaleDateString("tr-TR")}\n` +
      `📅 Bitiş Tarihi: ${expiresFormatted}\n\n` +
      `Gold üyeliğinizle birlikte aşağıdaki avantajlardan yararlanabilirsiniz:\n\n` +
      `• ⭐ Öne çıkan işletme rozeti — Konaklama listesinde ayrı bölümde gösterim\n` +
      `• 🔝 Arama sonuçlarında üst sıra — Rakiplerinizin önünde listeleme\n` +
      `• 📸 Sınırsız fotoğraf galerisi\n` +
      `• 📊 Özel istatistik paneli\n` +
      `• 🎯 Öncelikli müşteri desteği\n` +
      `• 📱 Sosyal medya paylaşımı\n\n` +
      `İşletmeniz artık "Öne Çıkan İşletmeler" bölümünde altın rozet ile görünmektedir.\n\n` +
      `Herhangi bir sorunuz için bizimle iletişime geçebilirsiniz.\n\n` +
      `İyi çalışmalar,\n` +
      `Türkiye Seyahat Rehberi Ekibi`
    );
    setTimeout(() => {
      window.open(`mailto:${req.email}?subject=${subject}&body=${body}`, "_blank");
    }, 500);
  };

  const handleRejectGoldRequest = (id: string) => {
    const remaining = goldRequests.filter((r) => r.id !== id);
    setGoldRequests(remaining);
    localStorage.setItem(GOLD_REQUESTS_KEY, JSON.stringify(remaining));
    showToast("Gold başvurusu reddedildi.");
  };

  const loadMemberships = () => {
    // Load from approved accommodations and merge with membership data
    const accApps: any[] = JSON.parse(localStorage.getItem("pendingAccommodations") || "[]")
      .filter((a: any) => a.status === "approved");
    const existing: MembershipRecord[] = JSON.parse(localStorage.getItem(MEMBERSHIP_KEY) || "[]");

    // Merge: add new approved accommodations that don't have a membership record yet
    const existingIds = new Set(existing.map((m) => m.id));
    const newRecords: MembershipRecord[] = accApps
      .filter((a) => !existingIds.has(a.id))
      .map((a) => ({
        id: a.id,
        businessName: a.businessName,
        ownerName: a.ownerName,
        email: a.email,
        phone: a.phone,
        city: a.city,
        businessType: a.businessType,
        tier: "free" as MembershipTier,
        status: "active" as MembershipStatus,
        appliedAt: a.appliedAt,
        activatedAt: a.appliedAt,
      }));

    const merged = [...existing, ...newRecords];
    if (newRecords.length > 0) {
      localStorage.setItem(MEMBERSHIP_KEY, JSON.stringify(merged));
    }
    setMemberships(merged);
  };

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const updateMembership = (id: string, changes: Partial<MembershipRecord>) => {
    const updated = memberships.map((m) => m.id === id ? { ...m, ...changes } : m);
    setMemberships(updated);
    localStorage.setItem(MEMBERSHIP_KEY, JSON.stringify(updated));
    setSelected((prev) => prev?.id === id ? { ...prev, ...changes } : prev);
  };

  const handleUpgradeToGold = (m: MembershipRecord) => {
    const now = new Date();
    const expires = new Date(now);
    expires.setFullYear(expires.getFullYear() + 1);
    updateMembership(m.id, {
      tier: "gold",
      status: "active",
      activatedAt: now.toISOString(),
      expiresAt: expires.toISOString(),
    });
    showToast(`"${m.businessName}" Gold üyeliğe yükseltildi!`);
  };

  const handleDowngradeToFree = (m: MembershipRecord) => {
    updateMembership(m.id, { tier: "free", expiresAt: undefined });
    showToast(`"${m.businessName}" Ücretsiz üyeliğe düşürüldü.`);
  };

  const handleToggleStatus = (m: MembershipRecord) => {
    const newStatus: MembershipStatus = m.status === "active" ? "inactive" : "active";
    updateMembership(m.id, { status: newStatus });
    showToast(`"${m.businessName}" ${newStatus === "active" ? "aktif edildi" : "pasif yapıldı"}.`);
  };

  const handleSaveNotes = (m: MembershipRecord) => {
    updateMembership(m.id, { notes: editNotes });
    setShowNoteEdit(false);
    showToast("Not kaydedildi.");
  };

  const filtered = memberships.filter((m) => {
    const matchTier = filterTier === "all" || m.tier === filterTier;
    const matchStatus = filterStatus === "all" || m.status === filterStatus;
    return matchTier && matchStatus;
  });

  const goldCount = memberships.filter((m) => m.tier === "gold").length;
  const freeCount = memberships.filter((m) => m.tier === "free").length;
  const activeCount = memberships.filter((m) => m.status === "active").length;

  return (
    <div className="relative">
      {toast && (
        <div className="fixed top-20 right-4 z-50 bg-emerald-500 text-white px-4 py-3 rounded-xl text-sm font-medium flex items-center gap-2">
          <i className="ri-checkbox-circle-line"></i> {toast}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
            Üyelik Yönetimi
          </h2>
          <p className="text-stone-400 text-xs mt-0.5">Onaylı işletmelerin üyelik planlarını yönetin</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-stone-100 p-4 text-center">
          <p className="text-2xl font-bold text-stone-900">{memberships.length}</p>
          <p className="text-xs text-stone-400 mt-1">Toplam Üye</p>
        </div>
        <div className="bg-amber-50 rounded-xl border border-amber-100 p-4 text-center">
          <p className="text-2xl font-bold text-amber-600">{goldCount}</p>
          <p className="text-xs text-stone-400 mt-1 flex items-center justify-center gap-1">
            <i className="ri-vip-crown-line text-amber-500"></i> Gold Üye
          </p>
        </div>
        <div className="bg-stone-50 rounded-xl border border-stone-100 p-4 text-center">
          <p className="text-2xl font-bold text-stone-600">{freeCount}</p>
          <p className="text-xs text-stone-400 mt-1">Ücretsiz Üye</p>
        </div>
        <div className="bg-emerald-50 rounded-xl border border-emerald-100 p-4 text-center">
          <p className="text-2xl font-bold text-emerald-600">{activeCount}</p>
          <p className="text-xs text-stone-400 mt-1">Aktif Üye</p>
        </div>
      </div>

      {/* Plan Comparison */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div className="bg-stone-50 rounded-2xl border border-stone-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 flex items-center justify-center bg-stone-200 rounded-lg">
              <i className="ri-user-line text-stone-600"></i>
            </div>
            <div>
              <p className="font-bold text-stone-900 text-sm">Ücretsiz Plan</p>
              <p className="text-xs text-stone-400">Temel özellikler</p>
            </div>
          </div>
          <ul className="space-y-1.5">
            {freeBenefits.map((b) => (
              <li key={b} className="flex items-center gap-2 text-xs text-stone-600">
                <i className="ri-check-line text-emerald-500"></i> {b}
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-amber-50 rounded-2xl border border-amber-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 flex items-center justify-center bg-amber-200 rounded-lg">
              <i className="ri-vip-crown-line text-amber-700"></i>
            </div>
            <div>
              <p className="font-bold text-amber-900 text-sm">Gold Plan</p>
              <p className="text-xs text-amber-600">Tüm özellikler + öncelik</p>
            </div>
          </div>
          <ul className="space-y-1.5">
            {goldBenefits.map((b) => (
              <li key={b} className="flex items-center gap-2 text-xs text-amber-800">
                <i className="ri-vip-crown-line text-amber-500 text-xs"></i> {b}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Tab: Üyeler / Gold Başvuruları */}
      <div className="flex items-center gap-1 bg-stone-100 rounded-xl p-1 mb-5 w-fit">
        {(["members", "gold-requests"] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex items-center gap-1.5 text-xs px-4 py-2 rounded-lg font-medium transition-all cursor-pointer whitespace-nowrap ${
              activeTab === tab ? "bg-white text-stone-800" : "text-stone-500 hover:text-stone-700"
            }`}
          >
            {tab === "gold-requests" && <i className="ri-vip-crown-fill text-amber-500"></i>}
            {tab === "members" ? "Üye Listesi" : `Gold Başvuruları`}
            {tab === "gold-requests" && goldRequests.length > 0 && (
              <span className="bg-amber-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full ml-0.5">
                {goldRequests.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Gold Requests Tab */}
      {activeTab === "gold-requests" && (
        <div className="space-y-3">
          {goldRequests.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-stone-100">
              <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 bg-amber-50 rounded-full">
                <i className="ri-vip-crown-line text-amber-400 text-2xl"></i>
              </div>
              <p className="text-stone-500 font-medium text-sm">Bekleyen Gold başvurusu yok</p>
              <p className="text-stone-400 text-xs mt-1">Yeni başvurular burada görünecek</p>
            </div>
          ) : (
            goldRequests.map((req) => (
              <div key={req.id} className="bg-white rounded-2xl border border-amber-200 p-5">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-stone-900 text-sm">{req.businessName}</h3>
                      <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full font-semibold flex items-center gap-1">
                        <i className="ri-vip-crown-fill text-xs"></i> Gold Talebi
                      </span>
                    </div>
                    <p className="text-xs text-stone-500">{req.city} · {req.ownerName}</p>
                  </div>
                  <p className="text-xs text-stone-400 whitespace-nowrap">
                    {new Date(req.appliedAt).toLocaleDateString("tr-TR")}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="bg-stone-50 rounded-lg p-2.5">
                    <p className="text-xs text-stone-400">E-posta</p>
                    <p className="text-xs font-medium text-stone-700 truncate">{req.email}</p>
                  </div>
                  <div className="bg-stone-50 rounded-lg p-2.5">
                    <p className="text-xs text-stone-400">Telefon</p>
                    <p className="text-xs font-medium text-stone-700">{req.phone}</p>
                  </div>
                  <div className="bg-stone-50 rounded-lg p-2.5">
                    <p className="text-xs text-stone-400">Mevcut Üyelik</p>
                    <p className="text-xs font-medium text-stone-700">{req.currentMember || "Belirtilmedi"}</p>
                  </div>
                  {req.message && (
                    <div className="bg-stone-50 rounded-lg p-2.5 col-span-2">
                      <p className="text-xs text-stone-400">Mesaj</p>
                      <p className="text-xs font-medium text-stone-700">{req.message}</p>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleApproveGoldRequest(req)}
                    disabled={approvingId === req.id}
                    className={`flex items-center gap-1.5 px-4 py-2 text-white text-xs font-bold rounded-xl transition-all cursor-pointer whitespace-nowrap ${
                      approvingId === req.id
                        ? "bg-emerald-500"
                        : "bg-gradient-to-r from-amber-500 to-yellow-400 hover:from-amber-600 hover:to-yellow-500"
                    }`}
                  >
                    {approvingId === req.id ? (
                      <><i className="ri-checkbox-circle-fill"></i> Onaylandı! E-posta açılıyor...</>
                    ) : (
                      <><i className="ri-vip-crown-fill"></i> Gold Onayla</>
                    )}
                  </button>
                  <button
                    onClick={() => handleRejectGoldRequest(req.id)}
                    className="flex items-center gap-1.5 px-4 py-2 bg-stone-100 hover:bg-stone-200 text-stone-600 text-xs font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-close-line"></i> Reddet
                  </button>
                  <a
                    href={`mailto:${req.email}?subject=Gold Üyelik Başvurunuz Hakkında&body=Sayın ${req.ownerName},%0A%0AGold üyelik başvurunuzu aldık. En kısa sürede sizinle iletişime geçeceğiz.%0A%0Aİyi çalışmalar,%0ATürkiye Seyahat Rehberi Ekibi`}
                    className="flex items-center gap-1.5 px-4 py-2 bg-stone-50 hover:bg-stone-100 text-stone-600 text-xs font-semibold rounded-xl border border-stone-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-mail-send-line"></i> E-posta
                  </a>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Members Tab */}
      {activeTab === "members" && (
        <>
      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="flex items-center bg-stone-100 rounded-full p-1 gap-1">
          {(["all", "free", "gold"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setFilterTier(t)}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap ${
                filterTier === t ? "bg-white text-stone-800" : "text-stone-500 hover:text-stone-700"
              }`}
            >
              {t === "all" ? "Tümü" : t === "gold" ? "Gold" : "Ücretsiz"}
            </button>
          ))}
        </div>
        <div className="flex items-center bg-stone-100 rounded-full p-1 gap-1">
          {(["all", "active", "inactive"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap ${
                filterStatus === s ? "bg-white text-stone-800" : "text-stone-500 hover:text-stone-700"
              }`}
            >
              {s === "all" ? "Tüm Durum" : s === "active" ? "Aktif" : "Pasif"}
            </button>
          ))}
        </div>
      </div>

      {memberships.length === 0 && activeTab === "members" ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-stone-100">
          <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 bg-stone-100 rounded-full">
            <i className="ri-vip-crown-line text-stone-400 text-2xl"></i>
          </div>
          <p className="text-stone-500 font-medium text-sm">Henüz onaylı üye yok</p>
          <p className="text-stone-400 text-xs mt-1">Konaklama başvurularını onayladıkça burada görünecek</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* List */}
          <div className="lg:col-span-1 space-y-2">
            {filtered.map((m) => (
              <button
                key={m.id}
                onClick={() => { setSelected(m); setEditNotes(m.notes || ""); setShowNoteEdit(false); }}
                className={`w-full text-left p-4 rounded-xl border transition-all cursor-pointer ${
                  selected?.id === m.id ? "border-amber-400 bg-amber-50" : "border-stone-100 bg-white hover:border-amber-200"
                }`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-semibold text-stone-900 text-sm leading-snug">{m.businessName}</h3>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${tierColors[m.tier]}`}>
                      {m.tier === "gold" && <i className="ri-vip-crown-line mr-0.5 text-xs"></i>}
                      {tierLabels[m.tier]}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-stone-500">{m.city} · {m.businessType}</p>
                <div className="flex items-center justify-between mt-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[m.status]}`}>
                    {m.status === "active" ? "Aktif" : "Pasif"}
                  </span>
                  {m.expiresAt && (
                    <p className="text-xs text-stone-400">
                      {new Date(m.expiresAt) > new Date()
                        ? `${Math.ceil((new Date(m.expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24))} gün kaldı`
                        : "Süresi doldu"}
                    </p>
                  )}
                </div>
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="text-center py-10 bg-white rounded-xl border border-stone-100">
                <p className="text-stone-400 text-sm">Bu filtreye uygun üye yok</p>
              </div>
            )}
          </div>

          {/* Detail */}
          <div className="lg:col-span-2">
            {selected ? (
              <div className="bg-white rounded-2xl border border-stone-100 p-6 space-y-5">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {selected.businessName}
                    </h3>
                    <p className="text-stone-400 text-sm mt-0.5">{selected.city} · {selected.businessType}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-3 py-1 rounded-full font-semibold ${tierColors[selected.tier]}`}>
                      {selected.tier === "gold" && <i className="ri-vip-crown-line mr-1 text-xs"></i>}
                      {tierLabels[selected.tier]}
                    </span>
                    <span className={`text-xs px-3 py-1 rounded-full font-medium ${statusColors[selected.status]}`}>
                      {selected.status === "active" ? "Aktif" : "Pasif"}
                    </span>
                  </div>
                </div>

                {/* Info Grid */}
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: "Yetkili", value: selected.ownerName },
                    { label: "E-posta", value: selected.email },
                    { label: "Telefon", value: selected.phone },
                    { label: "Katılım", value: new Date(selected.appliedAt).toLocaleDateString("tr-TR") },
                    { label: "Aktivasyon", value: selected.activatedAt ? new Date(selected.activatedAt).toLocaleDateString("tr-TR") : "—" },
                    { label: "Gold Bitiş", value: selected.expiresAt ? new Date(selected.expiresAt).toLocaleDateString("tr-TR") : "—" },
                  ].map((item) => (
                    <div key={item.label} className="bg-stone-50 rounded-lg p-3">
                      <p className="text-xs text-stone-400 mb-0.5">{item.label}</p>
                      <p className="text-sm font-semibold text-stone-800 truncate">{item.value}</p>
                    </div>
                  ))}
                </div>

                {/* Current Plan Benefits */}
                <div className={`rounded-xl p-4 border ${selected.tier === "gold" ? "bg-amber-50 border-amber-200" : "bg-stone-50 border-stone-200"}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`w-7 h-7 flex items-center justify-center rounded-lg ${selected.tier === "gold" ? "bg-amber-200" : "bg-stone-200"}`}>
                      <i className={`${tierIcons[selected.tier]} text-sm ${selected.tier === "gold" ? "text-amber-700" : "text-stone-600"}`}></i>
                    </div>
                    <p className={`text-sm font-semibold ${selected.tier === "gold" ? "text-amber-900" : "text-stone-700"}`}>
                      Mevcut Plan: {tierLabels[selected.tier]}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {(selected.tier === "gold" ? goldBenefits : freeBenefits).map((b) => (
                      <div key={b} className="flex items-center gap-1.5 text-xs">
                        <i className={`ri-check-line ${selected.tier === "gold" ? "text-amber-500" : "text-emerald-500"}`}></i>
                        <span className={selected.tier === "gold" ? "text-amber-800" : "text-stone-600"}>{b}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-stone-600">Admin Notu</p>
                    <button
                      onClick={() => setShowNoteEdit((v) => !v)}
                      className="text-xs text-amber-600 hover:text-amber-700 cursor-pointer whitespace-nowrap"
                    >
                      {showNoteEdit ? "İptal" : "Düzenle"}
                    </button>
                  </div>
                  {showNoteEdit ? (
                    <div>
                      <textarea
                        value={editNotes}
                        onChange={(e) => setEditNotes(e.target.value)}
                        rows={3}
                        maxLength={300}
                        placeholder="Bu üye hakkında not ekleyin..."
                        className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors resize-none"
                      />
                      <button
                        onClick={() => handleSaveNotes(selected)}
                        className="mt-2 px-4 py-2 bg-stone-900 text-white text-xs font-semibold rounded-lg cursor-pointer whitespace-nowrap hover:bg-stone-700 transition-colors"
                      >
                        Kaydet
                      </button>
                    </div>
                  ) : (
                    <p className="text-sm text-stone-500 bg-stone-50 rounded-xl p-3 min-h-[60px]">
                      {selected.notes || <span className="text-stone-300 italic">Not eklenmemiş</span>}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="pt-4 border-t border-stone-100 space-y-3">
                  <p className="text-xs font-semibold text-stone-600 mb-2">Üyelik İşlemleri</p>
                  <div className="flex flex-wrap gap-2">
                    {/* Gold Toggle — büyük ve belirgin */}
                    <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 w-full ${
                      selected.tier === "gold"
                        ? "bg-amber-50 border-amber-300"
                        : "bg-stone-50 border-stone-200"
                    }`}>
                      <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${
                        selected.tier === "gold" ? "bg-amber-200" : "bg-stone-200"
                      }`}>
                        {selected.tier === "gold"
                          ? <i className="ri-vip-crown-fill text-lg text-amber-700"></i>
                          : <i className="ri-vip-crown-line text-lg text-stone-500"></i>
                        }
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-bold ${selected.tier === "gold" ? "text-amber-900" : "text-stone-700"}`}>
                          Gold Üyelik
                        </p>
                        <p className="text-xs text-stone-400">
                          {selected.tier === "gold"
                            ? selected.expiresAt
                              ? `Bitiş: ${new Date(selected.expiresAt).toLocaleDateString("tr-TR")}`
                              : "Aktif"
                            : "Şu an ücretsiz planda"}
                        </p>
                      </div>
                      {/* Toggle switch */}
                      <button
                        onClick={() => selected.tier === "gold" ? handleDowngradeToFree(selected) : handleUpgradeToGold(selected)}
                        className={`relative w-12 h-6 rounded-full transition-all duration-300 cursor-pointer flex-shrink-0 ${
                          selected.tier === "gold" ? "bg-amber-500" : "bg-stone-300"
                        }`}
                        title={selected.tier === "gold" ? "Gold üyeliği kaldır" : "Gold üyeliği aç"}
                      >
                        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all duration-300 ${
                          selected.tier === "gold" ? "left-6" : "left-0.5"
                        }`}></span>
                      </button>
                      {selected.tier === "gold" ? (
                        <button
                          onClick={() => handleDowngradeToFree(selected)}
                          className="flex items-center gap-1.5 px-3 py-2 bg-rose-50 hover:bg-rose-100 text-rose-600 text-xs font-semibold rounded-xl border border-rose-200 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-close-circle-line"></i> Kaldır
                        </button>
                      ) : (
                        <button
                          onClick={() => handleUpgradeToGold(selected)}
                          className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-amber-500 to-yellow-400 hover:from-amber-600 hover:to-yellow-500 text-white text-xs font-bold rounded-xl transition-all cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-vip-crown-fill"></i> Aç
                        </button>
                      )}
                    </div>

                    {/* Active / Inactive Toggle */}
                    <button
                      onClick={() => handleToggleStatus(selected)}
                      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap ${
                        selected.status === "active"
                          ? "bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200"
                          : "bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200"
                      }`}
                    >
                      {selected.status === "active" ? (
                        <><i className="ri-pause-circle-line"></i> Üyeliği Askıya Al</>
                      ) : (
                        <><i className="ri-play-circle-line"></i> Üyeliği Aktif Et</>
                      )}
                    </button>

                    {/* Email notification */}
                    <button
                      onClick={() => {
                        const subject = selected.tier === "gold"
                          ? `"${selected.businessName}" — Gold Üyeliğiniz Aktif!`
                          : `"${selected.businessName}" — Üyelik Bilgilendirmesi`;
                        const body = selected.tier === "gold"
                          ? `Sayın ${selected.ownerName},\n\nİşletmeniz "${selected.businessName}" Gold üyeliğe yükseltilmiştir.\n\nGold üyelik avantajlarınız:\n${goldBenefits.map((b) => `• ${b}`).join("\n")}\n\nİyi çalışmalar,\nTürkiye Seyahat Rehberi Ekibi`
                          : `Sayın ${selected.ownerName},\n\nİşletmeniz "${selected.businessName}" üyelik durumu güncellenmiştir.\n\nDetaylar için bizimle iletişime geçebilirsiniz.\n\nİyi çalışmalar,\nTürkiye Seyahat Rehberi Ekibi`;
                        window.open(`mailto:${selected.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, "_blank");
                      }}
                      className="flex items-center gap-2 px-4 py-2.5 bg-stone-50 hover:bg-stone-100 text-stone-600 text-sm font-semibold rounded-xl border border-stone-200 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-mail-send-line"></i> E-posta Gönder
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-stone-100 h-64 flex flex-col items-center justify-center">
                <div className="w-12 h-12 flex items-center justify-center mb-3">
                  <i className="ri-vip-crown-line text-stone-300 text-3xl"></i>
                </div>
                <p className="text-stone-400 text-sm">Detayları görmek için bir üye seçin</p>
              </div>
            )}
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default MembershipManager;
